import {
  capitalize,
  Divider,
  Edit1Icon,
  GKButton,
  Grid2,
  Show,
  Tooltip,
  Typography,
} from "components";
import { OrderSimulationForm } from "interfaces/OrderSimulation";
import { useFormContext } from "react-hook-form";

export const AllocationByModel = ({
  referenceType,
  weightageDistributionMethod,
  allocationVersion,
}: {
  referenceType: { name: string; value: string }[];
  weightageDistributionMethod: { name: string; value: string }[];
  allocationVersion: { id: number; version: string }[];
}) => {
  const form = useFormContext<OrderSimulationForm>();

  return (
    <Grid2
      container
      size={12}
      p={"8px 16px"}
      borderRadius={1}
      bgcolor={"var(--grey-bg-100)"}
      spacing={3}
      display={"flex"}
      alignItems={"center"}
    >
      <Grid2
        container
        size={10}
        spacing={3}
        display={"flex"}
        alignItems={"center"}
      >
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={600} color="var(--black-900)">
            Allocation by Model:
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Model Reference Weightage
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {capitalize(form.getValues("allocation.useCurrentAllocation")) ||
              "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Allocation Reference Value
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {referenceType.find(
              (item) =>
                item?.value === form.getValues("allocation.rebalanceUsing"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Weightage Distribution Method
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {weightageDistributionMethod.find(
              (item) =>
                item.value ===
                form.getValues("allocation.weightageDistributionMethod"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Selected Model
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {allocationVersion?.find(
              (item) => item?.id === form.getValues("allocation.allocVersion"),
            )?.version || "-"}
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={2} display={"flex"} justifyContent={"flex-end"}>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<Edit1Icon />}
          onClick={() =>
            form.setValue("extraThings.rightVisibleTable", "EDIT_PORTFOLIO")
          }
        >
          Edit Details
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

export const AllocationByCompany = ({
  referenceType,
}: {
  referenceType: { name: string; value: string }[];
}) => {
  const form = useFormContext<OrderSimulationForm>();

  const formattedCompanyNames = (() => {
    const names = form
      .getValues("allocation.companies")
      .map((c: { companyName: string }) => c.companyName);

    if (names?.length <= 2) return names?.join(", ");
    return `${names[0]}, ${names[1]}... +${names?.length - 2} more`;
  })();

  return (
    <Grid2
      container
      size={12}
      p={"8px 16px"}
      borderRadius={1}
      bgcolor={"var(--grey-bg-100)"}
      spacing={3}
      display={"flex"}
      alignItems={"center"}
    >
      <Grid2
        container
        size={10}
        spacing={3}
        display={"flex"}
        alignItems={"center"}
      >
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={600} color="var(--black-900)">
            Allocation by Companies:
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={12} fontWeight={500} color="var(--grey-700)">
            Allocation Reference Value
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {referenceType.find(
              (item) =>
                item.value === form.getValues("allocation.rebalanceUsing"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"} display={"flex"} alignItems={"center"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size="auto">
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Selected Company
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {formattedCompanyNames || "-"}
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={2} display={"flex"} justifyContent={"flex-end"}>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<Edit1Icon />}
          onClick={() =>
            form.setValue("extraThings.rightVisibleTable", "EDIT_COMPANY")
          }
        >
          Edit Details
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

interface Props {
  customProp: {
    title: string;
    navigate: string;
  };
}

export const ExitBy = ({ customProp }: Props) => {
  const form = useFormContext();

  return (
    <Grid2
      container
      size={12}
      p={"8px 16px"}
      borderRadius={1}
      bgcolor={"var(--grey-bg-100)"}
      spacing={3}
      display={"flex"}
      alignItems={"center"}
    >
      <Grid2
        size={12}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={14} fontWeight={600} color="var(--black-900)">
          {customProp?.title}
        </Typography>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<Edit1Icon />}
          onClick={() =>
            form.setValue("extraThings.rightVisibleTable", customProp?.navigate)
          }
        >
          Edit Details
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

export const RebalanceByModel = ({
  rebalanceBy,
  referenceType,
  weightageDistributionMethod,
  allocationVersion,
}: {
  rebalanceBy: string;
  referenceType: { name: string; value: string }[];
  weightageDistributionMethod: { name: string; value: string }[];
  allocationVersion: { id: number; version: string }[];
}) => {
  const form = useFormContext<OrderSimulationForm>();

  return (
    <Grid2
      container
      size={12}
      p={"8px 16px"}
      borderRadius={1}
      bgcolor={"var(--grey-bg-100)"}
      spacing={3}
      display={"flex"}
      alignItems={"center"}
    >
      <Grid2
        container
        size={10}
        spacing={3}
        display={"flex"}
        alignItems={"center"}
      >
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={600} color="var(--black-900)">
            Rebalance by Model:
          </Typography>
        </Grid2>

        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Model Reference Weightage
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {capitalize(form.getValues("allocation.useCurrentAllocation")) ||
              "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Rebalance Reference Value
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {referenceType.find(
              (item) =>
                item?.value === form.getValues("allocation.rebalanceUsing"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Weightage Distribution Method
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {weightageDistributionMethod.find(
              (item) =>
                item?.value ===
                form.getValues("allocation.weightageDistributionMethod"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Show.When isTrue={rebalanceBy === "company"}>
          <Grid2 size={"auto"}>
            <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
              Selected Company
            </Typography>
            <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
              {form.getValues("allocation.companies")[0]?.name.slice(0, 20) ||
                "-"}
              {form.getValues("allocation.companies")[0]?.name.length > 20 &&
                "..."}
            </Typography>
          </Grid2>
          <Grid2 size={"auto"}>
            <Divider orientation="vertical" sx={{ height: 20 }} />
          </Grid2>
        </Show.When>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Selected Model
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {allocationVersion?.find(
              (item) => item?.id === form.getValues("allocation.allocVersion"),
            )?.version || "-"}
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={2} display={"flex"} justifyContent={"flex-end"}>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<Edit1Icon />}
          onClick={() =>
            form.setValue(
              "extraThings.rightVisibleTable",
              rebalanceBy === "company" ? "SELECT_PORTFOLIO" : "EDIT_PORTFOLIO",
            )
          }
        >
          Edit Details
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

export const RebalanceByCustom = ({
  referenceType,
  customParameter,
}: {
  referenceType: { name: string; value: string }[];
  customParameter: { name: string; value: string }[];
}) => {
  const form = useFormContext<OrderSimulationForm>();

  const formatRebalanceCompany = (
    companies: Array<{ companyName: string }>,
    maxDisplay?: number,
    allDisplay?: boolean,
  ) => {
    if (!companies || !Array.isArray(companies)) return "";

    const names = companies.map((c: { companyName: string }) => c.companyName);

    if (allDisplay) {
      return names?.join(", ");
    }
    if (names?.length <= (maxDisplay || 1)) return names?.join(", ");
    return `${names[0]}... +${names?.length - (maxDisplay || 1)} more`;
  };

  return (
    <Grid2
      container
      size={12}
      p={"8px 16px"}
      borderRadius={1}
      bgcolor={"var(--grey-bg-100)"}
      spacing={3}
      display={"flex"}
      alignItems={"center"}
    >
      <Grid2
        container
        size={10}
        spacing={3}
        display={"flex"}
        alignItems={"center"}
      >
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={600} color="var(--black-900)">
            Rebalance by Custom:
          </Typography>
        </Grid2>

        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Rebalance Reference Value
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {referenceType.find(
              (item) =>
                item?.value === form.getValues("allocation.rebalanceUsing"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Exit Parameter
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {customParameter.find(
              (item) =>
                item?.value === form.getValues("allocation.exitParameter"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size={"auto"}>
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Buy Parameter
          </Typography>
          <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
            {customParameter.find(
              (item) =>
                item?.value === form.getValues("allocation.buyParameter"),
            )?.name || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size="auto">
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Selected Exit Company
          </Typography>
          <Tooltip
            arrow
            title={formatRebalanceCompany(
              form.getValues("allocation.sellCompanies"),
              0,
              true,
            )}
          >
            <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
              {formatRebalanceCompany(
                form.getValues("allocation.sellCompanies"),
              ) || "-"}
            </Typography>
          </Tooltip>
        </Grid2>
        <Grid2 size={"auto"}>
          <Divider orientation="vertical" sx={{ height: 20 }} />
        </Grid2>
        <Grid2 size="auto">
          <Typography fontSize={14} fontWeight={400} color="var(--grey-700)">
            Selected Buy Company
          </Typography>
          <Tooltip
            arrow
            title={formatRebalanceCompany(
              form.getValues("allocation.buyCompanies"),
              0,
              true,
            )}
          >
            <Typography fontWeight={600} fontSize={14} color="var(--grey-900)">
              {formatRebalanceCompany(
                form.getValues("allocation.buyCompanies"),
              ) || "-"}
            </Typography>
          </Tooltip>
        </Grid2>
      </Grid2>
      <Grid2 size={2} display={"flex"} justifyContent={"flex-end"}>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<Edit1Icon />}
          onClick={() =>
            form.setValue("extraThings.rightVisibleTable", "BUY_COMPANY")
          }
        >
          Edit Details
        </GKButton>
      </Grid2>
    </Grid2>
  );
};
